import createReReSelector from 're-reselect';
import { createSelector } from 'reselect';

import { DropdownPricePackageItem } from '../../components/DropdownPricePackageEditor/DropdownPricePackageEditor';
import { mapPricePackageDataToDropdownEditor } from '../../mappers/pricePackages/pricePackages';
import {
    getBannerLangDropdownId,
    getBannerLangDropdownItems,
    getBannerLangDropdownLabel,
    LanguageDropdownParams
} from '../../service/BannersLang/BannersLang';
import {
    getMetrikaContersDependableFieldsWarningText,
    METRIKA_COUNTERS_DEPENDABLE_FIELDS
} from '../../service/CampaignsEditHelpers/CampaignsEditHelpers';
import {
    EditCampaignData,
    EditCampaignDataValue,
    EditCampaignsConstants,
    EditCampaignValueField,
    EnabledFieldsMap
} from '../../typings/campaignsEdit';
import { ValidationErrorData } from '../../typings/common';
import { Campaign, CampaignType } from '../../typings/domain';
import { Goal } from '../../typings/goals';
import { GdContentLanguage } from '../../typings/graphql-grid';
import { MetrikaCounterEditorValue } from '../../typings/metrikaCounterEditor';
import { NotificationsValue } from '../../typings/notificationEditor';
import { PricePackageEditorValue, PricePackageId } from '../../typings/pricePackages';
import { RetargetingConditionDataForRateCorrections } from '../../typings/ratesCorrections';
import { GoalsDataState, State } from '../../typings/state';
import { DnaStrategyType, Strategy } from '../../typings/strategy';
import { TimeZone, TimeZoneGroup } from '../../typings/timeTargetingEditor';
import { VisitingCardValueEditCampaign } from '../../typings/VisitingCardEditor';

export const getCampaign = (state: State, campaignId: string): EditCampaignData =>
    state.editCampaigns.campaigns[campaignId];

export const getCampaignValue = (state: State, campaignId: string): EditCampaignDataValue =>
    state.editCampaigns.campaigns[campaignId].value;

export const getCampaignData = (state: State, campaignId: string): EditCampaignData => {
    return state.editCampaigns.campaigns[campaignId];
};

export const selectEnabledFieldsForCampaign = createReReSelector(
    getCampaignData,
    (campaignData: EditCampaignData): EnabledFieldsMap => {
        return campaignData.enabledFields;
    }
)((state: State, campaignId: string) => campaignId);

export const selectRetargetingConditionsDataForEditCampaigns = createSelector(
    (state: State) => state.editCampaigns.retargetingConditions,
    (
        retargetingConditionsData?: Array<RetargetingConditionDataForRateCorrections>
    ): Array<RetargetingConditionDataForRateCorrections> => {
        return retargetingConditionsData || [];
    }
);

export const selectCampaignDisabledIpsValue = createReReSelector(
    getCampaignValue,
    (campaignValue: EditCampaignDataValue): string[] => {
        return campaignValue[EditCampaignValueField.disabledIps] || [];
    }
)((state: State, campaignId: string) => campaignId);

export const selectCampaignBannedDomainsValue = createReReSelector(
    getCampaignValue,
    (campaignValue: EditCampaignDataValue): string[] => {
        return campaignValue[EditCampaignValueField.bannedDomains] || [];
    }
)((state: State, campaignId: string) => campaignId);

export const selectCampaignBannedVideoDomainsValue = createReReSelector(
    getCampaignValue,
    (campaignValue: EditCampaignDataValue): string[] => {
        return campaignValue[EditCampaignValueField.bannedVideoDomains] || [];
    }
)((state: State, campaignId: string) => campaignId);

export const selectCampaignAllowedPageIdsValue = createReReSelector(
    getCampaignValue,
    (campaignValue: EditCampaignDataValue): string[] => {
        return campaignValue[EditCampaignValueField.allowedPageIds] || [];
    }
)((state: State, campaignId: string) => campaignId);

export const selectCampaignNotificationsValue = createReReSelector(
    getCampaignValue,
    (campaignValue: EditCampaignDataValue): NotificationsValue => {
        return campaignValue[EditCampaignValueField.notifications];
    }
)((state: State, campaignId: string) => campaignId);

export const selectMetrikaGoals = createSelector(
    (state: State) => state.goalsData,
    (goalsData: GoalsDataState): Record<string, Goal> => {
        if (goalsData === undefined || Object.keys(goalsData).length === 0) {
            return {};
        }

        return goalsData.goals || {};
    }
);

export const selectCampaignBannersLanguageValue = createReReSelector(
    getCampaignValue,
    (campaignValue: EditCampaignDataValue): GdContentLanguage | undefined => {
        return campaignValue[EditCampaignValueField.bannersLanguage];
    }
)((state: State, campaignId: string) => campaignId);

export const selectCampaignBannersLanguageErrors = createReReSelector(getCampaign, (campaignValue: EditCampaignData):
    | ValidationErrorData[]
    | undefined => {
    return campaignValue.errors[EditCampaignValueField.bannersLanguage];
})((state: State, campaignId: string) => campaignId);

export const selectBannersLanguageDropdownParams = createSelector(
    selectCampaignBannersLanguageValue,
    selectCampaignBannersLanguageErrors,
    (selectedLanguage?: GdContentLanguage, errors?: ValidationErrorData[]): LanguageDropdownParams => {
        return {
            selectedId: getBannerLangDropdownId(selectedLanguage),
            label: getBannerLangDropdownLabel(selectedLanguage),
            items: getBannerLangDropdownItems(),
            errors
        };
    }
);

export const selectMetrikaCounterEditorValueFromCampaignValue = createReReSelector(
    getCampaignValue,
    (campaignValue: EditCampaignDataValue): MetrikaCounterEditorValue => {
        return {
            counters: campaignValue[EditCampaignValueField.metrikaCounters],
            markMetrikaLinks: campaignValue[EditCampaignValueField.markMetrikaLinks]
        };
    }
)((state: State, campaignId: string) => campaignId);

export const selectCampaignMetrikaCountersDependableFields = createReReSelector(
    getCampaign,
    (campaign: EditCampaignData): EditCampaignValueField[] => {
        const enabledFieldsForCampaign = campaign.enabledFields;

        return METRIKA_COUNTERS_DEPENDABLE_FIELDS.filter(field => enabledFieldsForCampaign[field]);
    }
)((state: State, campaignId: string) => campaignId);

export const selectCampaignMetrikaCountersWarningMessage = createReReSelector(
    selectMetrikaCounterEditorValueFromCampaignValue,
    selectCampaignMetrikaCountersDependableFields,
    (
        metrikaCounterEditorValue: MetrikaCounterEditorValue,
        dependableFields: EditCampaignValueField[]
    ): string | undefined => {
        return metrikaCounterEditorValue && metrikaCounterEditorValue.counters.length !== 0
            ? getMetrikaContersDependableFieldsWarningText(dependableFields)
            : undefined;
    }
)((state: State, campaignId: string) => campaignId);

export const selectTimeZoneGroups = createSelector(
    (state: State) => state.commonData.timeZoneGroups,
    (timeZoneGroups: TimeZoneGroup[]) => timeZoneGroups
);

export const selectCountryTimeZone = createSelector(
    selectTimeZoneGroups,
    (timeZoneGroups?: TimeZoneGroup[]): TimeZone[] | undefined => {
        if (timeZoneGroups === undefined) {
            return undefined;
        }

        return timeZoneGroups.map(timeZoneGroup => {
            return {
                id: timeZoneGroup.id,
                name: timeZoneGroup.name
            };
        });
    }
);

export const selectSelectedCountryTimeZone = createReReSelector(
    (state: State, idTimeZone: string) => idTimeZone,
    (state: State, idTimeZone: string) => selectTimeZoneGroups(state),
    (idTimeZone: string, timeZonesGroups?: TimeZoneGroup[]): TimeZone | undefined => {
        if (timeZonesGroups === undefined) {
            return undefined;
        }

        const selectedCountryTimeZone = timeZonesGroups.find(countryItem =>
            countryItem.timezones.some(timezone => timezone.id === idTimeZone)
        );

        return selectedCountryTimeZone
            ? {
                  id: selectedCountryTimeZone.id,
                  name: selectedCountryTimeZone.name
              }
            : undefined;
    }
)((state: State, idTimeZone: string) => idTimeZone);

export const selectTimeZoneMenuItems = createReReSelector(
    (state: State, idTimeZone: string) => idTimeZone,
    selectSelectedCountryTimeZone,
    (state: State, idTimeZone: string) => selectTimeZoneGroups(state),
    (
        idTimeZone: string,
        selectedCountryTimeZone?: TimeZone,
        timeZoneGroups?: TimeZoneGroup[]
    ): TimeZone[] | undefined => {
        if (timeZoneGroups === undefined || selectedCountryTimeZone === undefined) {
            return undefined;
        }

        const currentTimeZoneGroup = timeZoneGroups.find(
            timeZoneGroup => timeZoneGroup.id === selectedCountryTimeZone.id
        );

        if (currentTimeZoneGroup === undefined) {
            return undefined;
        }

        return currentTimeZoneGroup.timezones.map(timezone => {
            return {
                id: timezone.id,
                name: timezone.name
            };
        });
    }
)((state: State, idTimeZone: string) => idTimeZone);

export const selectSelectedTimeZone = createReReSelector(
    (state: State, idTimeZone: string) => idTimeZone,
    (state: State, idTimeZone: string) => selectTimeZoneMenuItems(state, idTimeZone),
    (idTimeZone: string, timeZoneMenuItems?: TimeZone[]): TimeZone | undefined => {
        if (timeZoneMenuItems === undefined) {
            return undefined;
        }

        return timeZoneMenuItems.find(item => item.id === idTimeZone);
    }
)((state: State, idTimeZone: string) => idTimeZone);

export const selectCampaignVisitingCard = createReReSelector(
    getCampaignValue,
    (campaignValue: EditCampaignDataValue): VisitingCardValueEditCampaign => {
        return campaignValue[EditCampaignValueField.visitingCard];
    }
)((state: State, campaignId: string) => campaignId);

export const selectEditCampaignsConstants = createSelector(
    (state: State) => state.editCampaigns.constants,
    (constants?: EditCampaignsConstants): EditCampaignsConstants => {
        if (constants === undefined) {
            throw new Error('Константы редактирования кампаний не существуют');
        }

        return constants;
    }
);

export const selectEnabledRateCorrectionsForCampaign = createReReSelector(
    (state: State, campaignId: string) => state.editCampaigns.campaigns[campaignId].type,
    (state: State, campaignId: string) =>
        state.editCampaigns.campaigns[campaignId].value[EditCampaignValueField.strategy],
    (state: State, campaignId: string) => state.editCampaigns.constants,
    (type: CampaignType, strategy: Strategy, constants: EditCampaignsConstants) => {
        if (!type || !constants) {
            return [];
        }

        return constants.allowedRateCorrectionsData[type];
    }
)((state: State, campaignId: string) => campaignId);

export const selectFirstCampaignForPromoEditCampaigns = createSelector(
    (state: State) => state.grid.gridData.data,
    (campaignRows: Campaign[]): string | undefined => {
        const campaign = campaignRows.find(campaignRow => {
            return campaignRow.type === CampaignType.TEXT && campaignRow.access.canEdit;
        });

        return campaign !== undefined ? campaign.id : undefined;
    }
);

export const selectDisabledRateCorrectionsForTimeTargetingEditCampaigns = createReReSelector(
    getCampaignValue,
    (campaignValue: EditCampaignDataValue): boolean => {
        const { strategy } = campaignValue;
        return strategy.type !== DnaStrategyType.MANUAL;
    }
)((state: State, campaignId: string) => campaignId);

export const selectPricePackageValueFromCampaignValue = createReReSelector(
    getCampaignValue,
    (campaignValue: EditCampaignDataValue): PricePackageId => {
        return String(campaignValue[EditCampaignValueField.pricePackageId]);
    }
)((state: State, campaignId: string) => campaignId);

export const selectPricePackagesDataForEditCampaigns = createSelector(
    (state: State) => state.editCampaigns.pricePackages,
    (pricePackages?: Array<PricePackageEditorValue>): Array<DropdownPricePackageItem> | undefined => {
        return mapPricePackageDataToDropdownEditor(pricePackages);
    }
);

export const selectPricePackageDataForEditCampaigns = createReReSelector(
    (state: State, priceId: string) => priceId,
    (state: State) => state.editCampaigns.pricePackages,
    (priceId: PricePackageId, pricePackages?: Array<PricePackageEditorValue>): PricePackageEditorValue | undefined => {
        if (!pricePackages) {
            return;
        }

        return pricePackages.find(item => item.id === priceId);
    }
)((state: State, priceId: string) => priceId);
